<template>
  <div class="notify-control">
    <popover v-model="showPopover" type="dropdown" no-padding theme="light" :max-width="500" right v-if="screen.breakPoint.tabPort">
      <template v-slot:activator>
        <div class="notify-control__inner d-flex align-center justify-center cursor-pointer">
          <div class="notify-control__icon">
            <div class="icon-notification icon--medium text-color-purple pa-05"></div>
            <div class="notify-control__chip" v-if="unreadCount > 0">
              <div class="notify-control__chip-count">
                {{ unreadCount > 9 ? "9+" : unreadCount }}
              </div>
            </div>
          </div>
          <div class="text--medium">{{$t('components.NotifyControl.Notifications')}}</div>
          <div class="notify-control__arrow icon-show animate-smooth text-color-space"
               :class="{ 'rotate-180': showPopover }"></div>
        </div>
      </template>

<!--      <div class="py-1 set-indent">Notifications</div>-->
<!--      <div class="divider"></div>-->
      <div class="notification-content">
        <div class="notification-content__inner py-2" v-for="item of computedNotifications" :key="'n-' + item.id" v-observe-visibility="(isVisible, entry) => visibilityChanged(isVisible, entry, item.id)">
          <template v-if="item.translatedContent">
            <div class="text--small text--medium text-color-black mb-1">{{ item.simpleDate }}</div>
            <div v-html="item.translatedContent"></div>
          </template>
        </div>
      </div>
    </popover>

    <v-modal v-model="showPopover" full fit-content animation="slide-up" position="bottom" no-padding handle-click-bg v-else>
      <template #activator="{ on }">
        <div class="notify-control__inner d-flex align-center justify-center cursor-pointer" @click="on">
          <div class="notify-control__icon">
            <div class="icon-notification icon--medium text-color-purple pa-05"></div>
            <div class="notify-control__chip" v-if="unreadCount > 0">
              <div class="notify-control__chip-count">
                {{ unreadCount > 9 ? "9+" : unreadCount }}
              </div>
            </div>
          </div>
          <div class="notify-control__arrow icon-show animate-smooth text-color-space"
               :class="{ 'rotate-180': showPopover }"></div>
        </div>
      </template>

      <template #header>
        {{$t('components.NotifyControl.Notifications')}}
      </template>

      <div class="notification-content">
        <div class="notification-content__inner py-2" v-for="item of computedNotifications" :key="'n-' + item.id" v-observe-visibility="(isVisible, entry) => visibilityChanged(isVisible, entry, item.id)">
          <template v-if="item.translatedContent">
            <div class="text--small text--medium text-color-black mb-1">{{ item.simpleDate }}</div>
            <div v-html="item.translatedContent"></div>
          </template>
        </div>
      </div>
    </v-modal>
  </div>
</template>

<script>
import Popover from '@/ui/modals/Popover'
import vModal from '@/ui/modals/vModal'

import getTranslate from '@/mixins/getTranslate'
import toSimpleDate from '@/mixins/toSimpleDate'
import profile from '@/mixins/profile'
import screen from '@/mixins/screen'

import { StaticNotificationsGQL } from '@/graphql/queries'

export default {
  data () {
    return {
      showPopover: false,
      loading: false,
      /**
       * @type {NotificationItem[]}
       */
      notifications: [],
      readNotifications: [],
      observer: null
    }
  },

  apollo: {
    /**
     * @typedef StaticNotification
     * @property {number} StaticNotification.id
     * @property {NotificationContent} StaticNotification.NotificationContent
     * @property {number} StaticNotification.date
     * @property {ShowNotification} StaticNotification.ShowNotification
     */

    /**
     * @typedef NotificationContent
     * @property {string} NotificationContent.title
     * @property {string} NotificationContent.key
     * @property {string} NotificationContent.description
     * @property {string} NotificationContent.body
     */

    /**
     * @typedef ShowNotification
     * @property {number[]} ShowNotification.countries
     * @property {number[]} ShowNotification.exceptCountries
     * @property {number[]} ShowNotification.partners
     * @property {number[]} ShowNotification.exceptPartners
     * @property {SpecialRule[]} ShowNotification.specialRules
     */

    /**
     * @typedef SpecialRule
     * @property {string} SpecialRule.prop
     * @property {string} SpecialRule.value
     */

    /**
     * @returns {StaticNotification[]}
     */
    staticNotifications: {
      query: StaticNotificationsGQL,
      skip () { return !this.profile.slug },
      update: data => data.merchant.notifications.staticNotifications
    }
  },

  components: {
    Popover,
    vModal
  },

  mixins: [getTranslate, toSimpleDate, profile, screen],

  methods: {
    visibilityChanged (isVisible, entry, id) {
      if (isVisible) {
        this.setNotificationsRead(id)
      }
    },

    async setNotificationsRead (id) {
      const index = this.readNotifications.findIndex(i => {
        return i === id
      })

      if (index === -1) {
        this.readNotifications.push(id)
      }

      await this.$storage.set({
        key: 'read_notifications',
        value: JSON.stringify(this.readNotifications)
      })
    }
  },

  computed: {
    profileAggregatorName () {
      return this.profile.aggregatorName
    },

    profileActive () {
      return this.profile.active
    },

    unreadCount () {
      const readNotifications = this.readNotifications
      return this.computedNotifications.filter(n => !readNotifications.some(id => n.id === id)).length
    },

    computedNotifications () {
      /**
       * @type {NotificationItem[]}
       */
      const staticNotifications = this.staticNotifications || []
      const notifications = staticNotifications.map(n => {
        n.simpleDate = this.toSimpleDate(n.date, true)
        // n.translatedContent = this.getTranslate(n.content)
        n.translatedContent = n.content.body
        return n
      })

      const profile = this.profile
      if (notifications.length === 0) {
        return []
      }

      const reqCountryId = profile.reqCountry && profile.reqCountry.id ? profile.reqCountry.id : null
      const partnerId = profile.id

      return notifications.filter(n => {
        let show = true

        if (!n.show) {
          return show
        }

        const hasCountriesRules = n.show.countries && n.show.countries.length
        const hasExceptCountriesRules = n.show.exceptCountries && n.show.exceptCountries.length
        const hasPartnersRules = n.show.partners && n.show.partners.length
        const hasExceptPartnersRules = n.show.exceptPartners && n.show.exceptPartners.length
        const hasSpecialRules = n.show.specialRules && n.show.specialRules.length

        const countriesRules = n.show.countries
        const exceptCountriesRules = n.show.exceptCountries
        const partnersRules = n.show.partners
        const exceptPartnersRules = n.show.exceptPartners
        const specialRules = n.show.specialRules

        if (
          (hasCountriesRules && !countriesRules.some(c => c === reqCountryId)) ||
          (hasExceptCountriesRules && exceptCountriesRules.some(c => c === reqCountryId)) ||
          (hasPartnersRules && !partnersRules.some(p => p === partnerId)) ||
          (hasExceptPartnersRules && exceptPartnersRules.some(p => p === partnerId)) ||
          (hasSpecialRules && specialRules.some(r => this[r.prop] !== r.value)) ||
          !n.translatedContent
        ) {
          show = false
        }

        return show
      })
    }
  },

  async created () {
    const readNotificationsFromStorage = await this.$storage.get({
      key: 'read_notifications'
    }).then(({ value }) => value)

    if (readNotificationsFromStorage && readNotificationsFromStorage.length) {
      this.readNotifications = JSON.parse(readNotificationsFromStorage)
    }
  },

  mounted () {}
}
</script>

<style lang="scss">
.notify-control {
  position: relative;

  &__arrow {
    font-size: 1rem;
    margin-left: .5rem;
  }

  &__icon {
    position: relative;
  }

  &__chip {
    position: absolute;
    background-color: $color-red;
    width: 1.6rem;
    height: 1.6rem;
    right: 0;
    top: 0;
    border-radius: 50%;
  }

  &__chip-count {
    position: absolute;
    color: $color-white;
    font-size: 1rem;
    font-weight: 500;
    top: 50%;
    left: 50%;
    transform: translate(-51%, -50%);
  }
}

.notification-content {
  font-size: 1.3rem;
  color: $color-space;

  p {
    font-size: 1.3rem;
    color: darken($color-space, 10);

    &:not(:last-child) {
      padding-bottom: 1rem;
    }
  }

  &__inner {
    border-bottom: $color-space-light 1px solid;

    &:hover {
      background-color: $color-grey-extra-light;
      cursor: default;
    }

    & > * {
      margin-left: $default-spacing-mobile;
      margin-right: $default-spacing-mobile;

      @include respond(tab-port) {
        margin-left: $default-spacing;
        margin-right: $default-spacing;
      }
    }
  }
}
</style>
